import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { SET_LANGUAGE } from '../component/store/actions/actions';
import { AppState } from '../component/store/reducers/index';
import { CurrentUser } from '../interface/currentUser.interface';
import { TimeLapses } from '../interface/data.interface';
import { UserRoles } from '../interface/users.interface';
import { environment } from '../../environments/environment';
import { mainFeatures } from '../shared/sidebar/menu-items';

@Injectable()
export class DataService {
  private userCache: Partial<CurrentUser> | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 30000; // 30 segundos
  private lastLogTimestamp: number = 0;
  private readonly LOG_INTERVAL = 60000; // Solo log cada minuto
  private lastUserHash: string = ''; // Hash del último usuario para comparación

  // Emite cuando cambia el building activo (login, refresh, switch de multisession).
  // Permite a otros servicios reaccionar (ej. SupportWhatsappService).
  private buildingChangedSubject = new BehaviorSubject<void>(undefined);

  constructor(protected injector: Injector, protected store: Store<any>) {}

  buildingChanged$(): Observable<void> {
    return this.buildingChangedSubject.asObservable();
  }

  private emitBuildingChanged(): void {
    this.buildingChangedSubject.next();
  }

  // Método para generar hash del usuario para comparación
  private generateUserHash(user: Partial<CurrentUser>): string {
    if (!user) return '';
    
    // Crear un objeto con los campos críticos para la comparación
    const criticalFields = {
      id: user.id,
      email: user.email,
      fbToken: user.fbToken,
      token: user.token,
      isAuth: user.isAuth,
      superUser: user.superUser,
      buildings: user.buildings?.map(b => ({
        id: b.id,
        status: b.status,
        userStatus: b.userStatus,
        config: b.config
      }))
    };
    
    return JSON.stringify(criticalFields);
  }

  // Método para verificar si el usuario ha cambiado realmente
  private hasUserChanged(): boolean {
    try {
      const currentUserStr = localStorage.getItem('currentUser');
      if (!currentUserStr) return true; // Si no hay usuario, considerar como cambio
      
      const currentUser = JSON.parse(currentUserStr);
      const currentHash = this.generateUserHash(currentUser);
      
      return currentHash !== this.lastUserHash;
    } catch (error) {
      console.error('Error comparing user hash:', error);
      return true; // En caso de error, considerar como cambio
    }
  }

  getUser(): Partial<CurrentUser> | null {
    const now = Date.now();
    
    // Verificar si el caché es válido temporalmente
    const isCacheTimeValid = this.userCache && (now - this.cacheTimestamp) < this.CACHE_DURATION;
    
    // Verificar si el usuario ha cambiado realmente
    const hasUserChanged = this.hasUserChanged();
    
    // Si el caché es válido temporalmente Y el usuario no ha cambiado
    if (isCacheTimeValid && !hasUserChanged) {
      return this.userCache;
    }

    try {
      let currentUser: string = localStorage.getItem('currentUser');
      if (currentUser) {
        let currentUserParsed: Partial<CurrentUser> = JSON.parse(currentUser);
        
        if (currentUserParsed && currentUserParsed.fbToken && currentUserParsed.isAuth) {
          if (currentUserParsed.superUser || (currentUserParsed.buildings && currentUserParsed.buildings.length > 0)) {
            if (now - this.lastLogTimestamp > this.LOG_INTERVAL) {
              this.lastLogTimestamp = now;
            }
            
            this.userCache = currentUserParsed;
            this.cacheTimestamp = now;
            this.lastUserHash = this.generateUserHash(currentUserParsed);
            
            return currentUserParsed;
          } else {
            console.warn('User missing buildings');
            this.clearCache();
            return null;
          }
        } else {
          console.warn('User missing required fields (fbToken or isAuth)');
          this.clearCache();
          return null;
        }
      } else {
        this.clearCache();
        return null;
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      this.clearCache();
      return null;
    }
  }

  setUser(user) {
    // Limpiar caché cuando se actualiza el usuario
    this.clearCache();
    // Asegurar que tenemos todos los campos necesarios
    if (user && user.fbToken) {
      // Verificar que el fbToken sea válido
      if (typeof user.fbToken === 'string' && user.fbToken.length > 0) {
        localStorage.setItem('currentUser', JSON.stringify(user));
        // Solo log cuando realmente se actualiza el usuario, no en cada llamada
        const now = Date.now();
        if (now - this.lastLogTimestamp > this.LOG_INTERVAL) {
          console.log('User saved successfully with fbToken:', user.fbToken.substring(0, 20) + '...');
          this.lastLogTimestamp = now;
        }
      } else {
        console.warn('Invalid fbToken, user not saved');
      }
     
    } else {
      console.warn('User missing fbToken, saving anyway:', user);
      localStorage.setItem('currentUser', JSON.stringify(user));
    }
    this.emitBuildingChanged();
  }

  getUserInMultisession(){
    return JSON.parse(localStorage.getItem('usersSessions')) ?? [];
  }

  setUserInMultisesion(user):void{
    let sessions:any[] = JSON.parse(localStorage.getItem('usersSessions'));
    if(sessions && sessions.length > 0){
      let foundIn = sessions.findIndex((s)=> s.email == user.email);
      if(foundIn != -1){
        sessions[foundIn] = user;
      }else{
        sessions.push(user);
      }
    }else{
      sessions = [user];
    }
    localStorage.setItem('usersSessions', JSON.stringify(sessions));
  }

  /**
   * Removes one account from `usersSessions` by id and/or email.
   * Does not touch `currentUser`.
   */
  removeUserFromMultisession(identity: { email?: string; id?: string }): void {
    const sessions = this.getUserInMultisession();
    if (!sessions.length) {
      return;
    }
    const emailNorm = (identity.email ?? '').toLowerCase().trim();
    const id = identity.id;
    const filtered = sessions.filter((session) => {
      const sameId = !!id && session.id === id;
      const sameEmail =
        !!emailNorm && (session.email ?? '').toLowerCase().trim() === emailNorm;
      return !(sameId || sameEmail);
    });
    if (filtered.length === 0) {
      localStorage.removeItem('usersSessions');
    } else {
      localStorage.setItem('usersSessions', JSON.stringify(filtered));
    }
  }

  getBuilding() {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user) {
      return user.buildings[0];
    }
    return null;
  }

  isProprietor(): boolean {
    const user = this.getUser();
    if (!user) return false;
    const top = user.roles?.includes(UserRoles.proprietor);
    const inBuilding = !!user.buildings?.[0]?.roles?.includes(UserRoles.proprietor);
    return !!(top || inBuilding);
  }

  /** Admin del edificio (prioridad sobre proprietor cuando el actor tiene ambos). */
  isBuildingAdmin(): boolean {
    const user = this.getUser();
    if (!user) return false;
    const top = user.roles?.includes(UserRoles.admin);
    const inBuilding = !!user.buildings?.[0]?.roles?.includes(UserRoles.admin);
    return !!(top || inBuilding);
  }

  /**
   * Roles de alcance edificio: pueden ver el building completo y filtrar por
   * cualquier unidad. No se les fuerza la unidad de sesión (VDS-3444).
   */
  hasBuildingScopedRole(): boolean {
    const user = this.getUser();
    if (!user) return false;
    const roles = [...(user.roles ?? []), ...(user.buildings?.[0]?.roles ?? [])];
    const buildingScopedRoles: string[] = [
      UserRoles.admin,
      UserRoles.customized,
      UserRoles.committee,
      UserRoles.vigilant,
      'kiosco',
    ];
    return roles.some((role) => buildingScopedRoles.includes(role));
  }

  /**
   * Propietario puro: tiene proprietor y ningún rol de edificio.
   * Un admin con unidad asignada NO es proprietor-only: puede listar el edificio
   * entero o filtrar por cualquier unidad; no se le fuerza la suya.
   */
  isProprietorOnly(): boolean {
    return this.isProprietor() && !this.hasBuildingScopedRole();
  }

  /** Rol personalizado (`customized`) en sesión o en el building actual. */
  isCustomized(): boolean {
    const user = this.getUser();
    if (!user) return false;
    const top = user.roles?.includes(UserRoles.customized);
    const inBuilding = !!user.buildings?.[0]?.roles?.includes(UserRoles.customized);
    return !!(top || inBuilding);
  }

  getSessionUnitId(): string | undefined {
    const user = this.getUser();
    if (!user?.buildings?.length) return undefined;
    const building = user.buildings[0];
    const unitId = building?.unitId ?? building?.unit;
    if (!unitId) return undefined;
    return typeof unitId === 'string' ? unitId : unitId?.id;
  }

  getProprietorUnitId(): string | null {
    // Solo proprietor puro: admin+proprietor no debe heredar filtro de unidad (VDS-3228).
    if (!this.isProprietorOnly()) return null;
    return this.getSessionUnitId() ?? null;
  }

  //Usado para los selects
  getProprietorUnitDisplay(): { id: string; number: string } | null {
    const uid = this.getProprietorUnitId();
    if (!uid) return null;
    const user = this.getUser();
    if (!user?.buildings?.length) return null;
    const building = user.buildings[0];
    const unit = building?.unit;
    const number = (unit && typeof unit === 'object' && unit.number) ? unit.number : (building?.unitNumber ?? uid);
    const id = (unit && typeof unit === 'object' && unit.id) ? unit.id : uid;
    return { id, number };
  }

  /**
   * VDS-3444: el propietario puro llama /proprietor/v1; el resto de roles /admin/v1.
   * El query/body es el mismo (R14).
   */
  public legacyRoleV1Url(resource: string): string {
    const role = this.isProprietorOnly() ? 'proprietor' : 'admin';
    return `${environment.apiUrl}/${role}/v1/${resource}`;
  }

  setBuilding(building) {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user && user.buildings?.length) {
      const previous = user.buildings[0];
      if (building.unitId == null && building.unit == null && (previous?.unitId != null || previous?.unit != null)) {
        building.unitId = previous.unitId ?? previous.unit;
        building.unit = previous.unit;
      }
      user.buildings[0] = building;
      localStorage.setItem('currentUser', JSON.stringify(user));
    } else if (user) {
      user.buildings = user.buildings || [];
      user.buildings[0] = building;
      localStorage.setItem('currentUser', JSON.stringify(user));
    }
    this.emitBuildingChanged();
  }

  getPlan() {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user) {
      if (user.buildings[0]) return user.buildings[0].plan;
      return null;
    }
    return null;
  }

  getUid() {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user) return user.uid;
    return null;
  }

  getLoading() {
    return this.store.select('appReducer');
  }

  getshowModal() {
    return this.store.select((state: AppState) => state.appReducer.showModal);
  }

  isLoading(obj) {
    this.store.dispatch({
      type: obj.action,
      payload: obj.payload,
    });
  }

  showModal(obj) {
    this.store.dispatch({
      type: obj.action,
      payload: obj.payload,
    });
  }

  getAppLanguage() {
    return localStorage.getItem('munily-language');
  }

  setAppLanguage(lang) {
    localStorage.setItem('munily-language', lang);
  }

  getLanguage() {
    let lang = 'es';
    this.store.select('appReducer').subscribe((items) => {
      lang = items.curLanguage;
    });
    return lang;
  }

  setLanguage(lang?) {
    const appLang = this.getAppLanguage();
    let curLang = '';

    if (!appLang) {
      this.setAppLanguage(lang);
      curLang = lang;
    } else if (appLang && appLang === lang) {
      curLang = appLang;
    } else if (appLang && appLang !== lang) {
      this.setAppLanguage(lang);
      curLang = lang;
    }
    this.store.dispatch({
      type: SET_LANGUAGE,
      payload: curLang,
    });
    
    return curLang;
  }

  getUserIsMacroCommunity(): boolean {
    const user = this.getUser();
    if (!user?.buildings?.length) return false;
    return user.buildings.every((building) => {
      if (building.config && building.config.macrocommunity) {
        return building.config.macrocommunity.enable ?? false;
      }
      return false;
    });
  }

  getBuildingIsMacroFather(): boolean {
    const user = this.getUser();
    if (!user?.buildings?.length) return false;
    return user.buildings.every((building) => {
      if (building.config && building.config.macrocommunity) {
        return !!(
          building.config.macrocommunity.enable &&
          building.config.macrocommunity.communities?.[0]?.type === 'macrocommunity'
        );
      }
      return false;
    });
  }

  getFeatures() {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user && user.features) {
      return user.features;
    }
    return null;
  }

  isFeatureActive(feature:mainFeatures){
    const features = this.getFeatures();
    if (features && features.length < 0) return false;
    const isActive = features.some(element => element.feature.indexOf(feature) > -1 && element.enable === true);
    return isActive;
  }

  getIntervalDates(time:TimeLapses, format:string):{ startDate: string, endDate: string }{
    let dates: any = { startDate: null, endDate: null };
    switch (time) {
      case TimeLapses.day:
      case TimeLapses.today:
        dates.startDate = moment.tz().startOf(TimeLapses.day).format(format);
        dates.endDate = moment.tz().endOf(TimeLapses.day).format(format);
      break;

      case TimeLapses.week:
        let today = moment.tz().clone().endOf(TimeLapses.week).toDate();
        dates.startDate = moment.tz().subtract(today.getDay() - 1, 'days').startOf(TimeLapses.week).format(format);
        dates.endDate = moment(today).format(format);
      break;

      case TimeLapses.month:
        let day = new Date().getDate();
        dates.startDate =
          day == 1
            ? moment.tz().startOf(TimeLapses.month).format(format)
            : moment.tz().subtract(day - 1, 'days').startOf(TimeLapses.month).format(format);
        dates.endDate = moment.tz().endOf(TimeLapses.month).format(format);
      break;

      case TimeLapses.year:
        dates.startDate = moment.tz().startOf(TimeLapses.year).format(format);
        dates.endDate = moment.tz().endOf(TimeLapses.day).format(format);
      break;

      default:
        dates.startDate = moment.tz().startOf(TimeLapses.day).format(format);
        dates.endDate = moment.tz().endOf(TimeLapses.day).format(format);
      break;
    }
    return dates;
  }

  getIntervalDays(type: string, format:string):{ startDate: string, endDate: string } {
    let today = moment.tz().endOf(TimeLapses.day).toDate(); 
    let dates: any = { 
      startDate: null, 
      endDate: today
    };

    switch (type) {
      case TimeLapses.day: //Last 24hours days
      case TimeLapses.today: //Last 24hours days
        dates.startDate = moment.tz().startOf(TimeLapses.day).format(format);
      break;

      case TimeLapses.week: //Last 7 days
        dates.startDate = moment.tz().subtract(1, TimeLapses.week).startOf(TimeLapses.day).format(format);
      break;

      case TimeLapses.month:  //Last 30 days
        dates.startDate = moment.tz().subtract(1, TimeLapses.month).startOf(TimeLapses.day).format(format);
      break;

      case TimeLapses.year:
        dates.startDate = moment.tz().subtract(1, TimeLapses.year).startOf(TimeLapses.day).format(format);
      break;
    }
    return dates;
  }

  getDatesWithinRange(startDate:string, endDate:string, dateFormat:string):string[]{
    const dates:string[] = [];
    let currentDate = moment(startDate, dateFormat);
    const finalDate = moment();
    
    while (currentDate.isSameOrBefore(finalDate)) {
      dates.push(currentDate.format(dateFormat));
      currentDate.add(1, 'day');
    }

    return dates;
  }

  getBuildingTimeZone(){
    let building = this.getBuilding();
    return building?.config?.timezone;
  }

  isChatEnable() {
    let building = this.getBuilding();
    return building.config.chatV2.enable ?? false;
  }

  getChatRoles() {
    let building = this.getBuilding();
    return building.config.chatV2.roles ?? [];
  }

  //RESTART SESSION
  restartSessions(){
    localStorage.clear();
    return null;
  }

  private clearCache(): void {
    this.userCache = null;
    this.cacheTimestamp = 0;
    this.lastLogTimestamp = 0;
    this.lastUserHash = ''; // Limpiar hash al borrar caché
  }

  // Método público para limpiar caché cuando sea necesario
  public clearUserCache(): void {
    this.clearCache();
  }

  // Método para verificar si el caché está activo
  public isCacheValid(): boolean {
    const now = Date.now();
    return this.userCache && (now - this.cacheTimestamp) < this.CACHE_DURATION && !this.hasUserChanged();
  }

  // Método para obtener usuario sin caché (útil para casos específicos)
  public getUserWithoutCache(): Partial<CurrentUser> | null {
    this.clearCache();
    return this.getUser();
  }

  // Método para forzar refrescamiento de sesión (útil para notificaciones del backend)
  public forceRefreshUser(): Partial<CurrentUser> | null {
    this.clearCache();
    return this.getUser();
  }

  // Método para verificar si la sesión necesita refrescamiento
  public needsSessionRefresh(): boolean {
    return this.hasUserChanged();
  }

  public updateUserDatabaseCertificationContent(enable: boolean) {
    const building = this.getBuilding();

    if (!building?.config?.certificationContent) {
      building.config.certificationContent = {};
    }
    building.config.certificationContent.enable = enable;
    this.setBuilding(building);
  }

  refreshMultisessionBuildingId(buildingId: string, building: any) {
    const sessions = this.getUserInMultisession();
    if (!sessions || sessions.length === 0) return;

    const newSessions = sessions.map((session) => {
      if (session.buildings.some((b) => b.id === buildingId)) {
        return {
          ...session,
          buildings: [building]
        };
      }
      return session;
    });

    localStorage.setItem('usersSessions', JSON.stringify(newSessions));
  }
}
